// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Read-only Response procedure list for Anomaly Detail.
//
// Deliberately not the same component as AlarmDetail's procedure section: that
// one carries per-SOP collapse state, a triage affordance. The SOPs are shown
// so the operator can read what response is bound to the rule before deciding
// whether to promote.
//
// Shares AlarmDetail.css's `ald-proc*` and `ald-steps` / `ald-step-*` classes,
// imported by the page — the step rows are styled there, for both screens.

import React from 'react';
import { Link } from 'react-router-dom';
import { sopDetailPath } from '../../services/sopPath';
import type { ResolvedSop } from '../../types/alarm';

interface Props {
  sops: ResolvedSop[];
  /** The rule lookup failed, so an empty list is unknown rather than none. */
  unavailable?: boolean;
  /** The anomaly's site, so a SOP opens under it (ADM reads per site). */
  siteId?: string;
}

export default function AnomalySopList({ sops, unavailable = false, siteId }: Props) {
  return (
    <section className="ald-card ald-proc" aria-labelledby="anom-proc-title">
      <div className="ald-proc-head">
        <div className="ald-proc-title" id="anom-proc-title">
          Response procedure
        </div>
      </div>

      {sops.length === 0 ? (
        <p className="ald-proc-empty">
          {unavailable
            ? "Couldn't load the linked procedures. Retry above."
            : 'No response procedure linked.'}
        </p>
      ) : (
        sops.map(sop => (
          <div key={sop.id} className="ald-proc-sop">
            <div className="ald-proc-sop-head">
              <div className="ald-proc-meta">
                <Link to={sopDetailPath(sop.id, siteId)}>{sop.id}</Link>
                {sop.title ? ` · ${sop.title}` : ''}
                {sop.version ? ` · ${sop.version}` : ''}
                {sop.labor ? ` · ${sop.labor}` : ''}
              </div>
            </div>
            <div className="ald-proc-sop-body">
              {sop.status === 'failed' ? (
                <p className="ald-proc-unavailable-note">This SOP could not be loaded.</p>
              ) : (
                <ol className="ald-steps">
                  {sop.steps.map((s, i) => (
                    <li key={i}>
                      <span className="ald-step-num">{String(i + 1).padStart(2, '0')}</span>
                      {s.verb && <span className="ald-step-verb">{s.verb}</span>}
                      <div className="ald-step-body">
                        <div className="ald-step-text">{s.instruction}</div>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        ))
      )}
    </section>
  );
}
